import React, { useState, useEffect } from 'react';
import {
    Edit,
    SimpleForm,
    TextInput,
    BooleanInput,
    SelectInput,
    required,
    useTranslate,
    useRedirect,
    useRecordContext,
    useNotify,
    FunctionField,
    FormDataConsumer,
    Toolbar,
    SaveButton,
} from 'react-admin';
import { Box, Typography, Alert, Card, CardContent, Link, IconButton, Chip } from '@mui/material';
import { GetApp, Memory, CloudDownload, CheckCircle, ArrowBack, Link as LinkIcon } from '@mui/icons-material';
import { tokenManager } from '../../utils/tokenManager';
import { API_CONFIG } from '../../config/api';
import { ServerErrorBinder } from '../common/ServerErrorBinder';
import { translateServerMessage } from '../../utils/translateServerMessage';

const FIRMWARE_KNOWN_FIELDS = new Set([
    'file', 'file_url', 'vendor', 'model', 'version', 'description', 'is_active', 'release_notes',
]);

const mapFirmwareBackendPath = (path: string): string =>
    path.replace(/^body\./, '').replace(/^params\./, '').replace(/^query\./, '');

const firmwareFieldLabelKey = (field: string): string =>
    FIRMWARE_KNOWN_FIELDS.has(field)
        ? `resources.charger-firmware.fields.${field}`
        : field;

const EditToolbar = () => (
    <Toolbar
        sx={{
            backgroundColor: '#FAFAFA',
            borderTop: '1px solid #E0E0E0',
            px: { xs: 2, sm: 3 },
            py: 2,
            mt: { xs: 2, sm: 4 },
            '& .MuiButton-root': {
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 600,
                px: 3,
                py: 1.5,
            },
        }}
    >
        <SaveButton
            variant="contained"
            sx={{
                backgroundColor: '#00C853',
                '&:hover': {
                    backgroundColor: '#00A843',
                    boxShadow: '0 4px 12px rgba(0,200,83,0.3)',
                },
            }}
        />
    </Toolbar>
);

const PageHeader = () => {
    const record = useRecordContext();
    const redirect = useRedirect();
    const translate = useTranslate();

    if (!record) return null;

    return (
        <Box sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            mb: { xs: 2, sm: 4 },
        }}>
            <IconButton
                onClick={() => redirect('/charger-firmware')}
                size="large"
                sx={{
                    flexShrink: 0,
                    backgroundColor: '#FFFFFF',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                    '&:hover': {
                        backgroundColor: '#F5F5F5',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
                    },
                }}
            >
                <ArrowBack />
            </IconButton>
            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontWeight: 600, mb: 0.5, fontSize: { xs: '1.25rem', sm: '2.125rem' }, wordBreak: 'break-word', overflowWrap: 'anywhere' }}>
                    {translate('resources.charger-firmware.edit.title')}
                </Typography>
                <Typography
                    variant="body1"
                    color="text.secondary"
                    sx={{
                        fontSize: { xs: '0.75rem', sm: '1rem' },
                        wordBreak: 'break-word',
                        overflowWrap: 'anywhere',
                    }}
                >
                    {record.version} • {record.file_name}
                </Typography>
            </Box>
        </Box>
    );
};

const FirmwareEdit = () => {
    const translate = useTranslate();
    const notify = useNotify();
    const [vendors, setVendors] = useState<string[]>([]);
    const [modelsByVendor, setModelsByVendor] = useState<{ [key: string]: string[] }>({});
    const [serverErrors, setServerErrors] = useState<Record<string, string>>({});

    // Fetch vendors and models from API
    useEffect(() => {
        const fetchVendorsAndModels = async () => {
            try {
                const token = await tokenManager.getAccessToken();
                if (!token) {
                    throw new Error('No authentication token available');
                }

                const response = await fetch(`${API_CONFIG.baseURL}/api/backend/charger-firmware/vendors-models`, {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                    },
                });

                if (!response.ok) {
                    const errorData = await response.json().catch(() => ({}));
                    throw new Error(errorData.error || 'Failed to fetch vendors and models');
                }

                const data = await response.json();
                setVendors(data.vendors);
                setModelsByVendor(data.modelsByVendor);
            } catch (error) {
                console.error('Failed to fetch vendors and models:', error);
                notify('resources.charger-firmware.notifications.error.load_vendors_models', { type: 'error' });
            }
        };

        fetchVendorsAndModels();
    }, [notify]);

    return (
        <Edit
            title={false}
            actions={false}
            component="div"
            redirect="list"
            mutationMode="pessimistic"
            transform={(data: any) => {
                // file_url is loaded into form state from the record on edit. For
                // uploaded firmwares, the API returned a resolved public URL —
                // sending that back as an update would trigger the backend's
                // "cannot change file URL of an uploaded firmware" guard. Strip
                // it for non-external records; external records keep the field
                // so the admin can actually edit the URL.
                if (!data?.is_external) {
                    const { file_url, file_name, file_size, ...rest } = data;
                    return rest;
                }
                const { file_name, file_size, ...rest } = data;
                return rest;
            }}
            mutationOptions={{
                onSuccess: () => {
                    setServerErrors({});
                    notify('resources.charger-firmware.notifications.updated', { type: 'success' });
                },
                onError: (error: any) => {
                    const apiError = error?.body || error;
                    const rawErrors = apiError?.errors as Record<string, string[] | string> | undefined;
                    const fieldErrors: Record<string, string> = {};
                    const summaryLines: string[] = [];
                    if (rawErrors) {
                        for (const [path, msgs] of Object.entries(rawErrors)) {
                            const rawMsg = Array.isArray(msgs) ? msgs[0] : String(msgs);
                            const translated = translateServerMessage(rawMsg, translate);
                            const field = mapFirmwareBackendPath(path);
                            fieldErrors[field] = translated;
                            const label = translate(firmwareFieldLabelKey(field), { _: field });
                            summaryLines.push(`• ${label}: ${translated}`);
                        }
                    }
                    setServerErrors(fieldErrors);

                    const headerKey = apiError?.errorCode
                        ? `errors.${apiError.errorCode}`
                        : 'validation.formHasErrors';
                    const header = translate(headerKey, { _: apiError?.message || 'Error' });

                    if (summaryLines.length > 0) {
                        notify(`${header}\n${summaryLines.join('\n')}`, {
                            type: 'error',
                            multiLine: true,
                            autoHideDuration: 10000,
                        });
                    } else {
                        notify(header, { type: 'error', autoHideDuration: 6000 });
                    }
                },
            }}
            sx={{
                maxWidth: '100%',
                overflow: 'hidden',
                backgroundColor: 'transparent',
                '& .RaEdit-main': {
                    backgroundColor: 'transparent',
                    boxShadow: 'none',
                },
            }}
        >
            <SimpleForm
                toolbar={<EditToolbar />}
                sx={{
                    '& .MuiPaper-root': {
                        backgroundColor: 'transparent',
                        boxShadow: 'none',
                    },
                    '& > .MuiCardContent-root, & > div > .MuiCardContent-root': {
                        px: { xs: 0, sm: 2 },
                        py: { xs: 1, sm: 2 },
                    },
                    padding: { xs: 0, sm: 'inherit' },
                }}
            >
                <ServerErrorBinder errors={serverErrors} />
                <Box sx={{ width: '100%', maxWidth: 800, margin: '0 auto', pb: { xs: 10, sm: 2 }, '& .MuiFormControl-root': { width: '100%' } }}>
                    <PageHeader />

                    {/* Basic Information Section */}
                    <Card sx={{ mb: { xs: 2, sm: 3 }, overflow: 'hidden' }}>
                        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                                <Memory sx={{ mr: 2, color: '#00C853' }} />
                                <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                                    {translate('resources.charger-firmware.sections.basic_info')}
                                </Typography>
                            </Box>

                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                <TextInput
                                    source="version"
                                    label={translate('resources.charger-firmware.fields.version')}
                                    helperText={translate('resources.charger-firmware.helpers.version')}
                                    validate={required()}
                                    fullWidth
                                />

                                <TextInput
                                    source="description"
                                    label={translate('resources.charger-firmware.fields.description')}
                                    helperText={translate('resources.charger-firmware.helpers.description')}
                                    multiline
                                    rows={3}
                                    fullWidth
                                />
                            </Box>
                        </CardContent>
                    </Card>

                    {/* File / URL Information Section */}
                    <Card sx={{ mb: { xs: 2, sm: 3 }, overflow: 'hidden' }}>
                        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                                <CloudDownload sx={{ mr: 2, color: '#00C853' }} />
                                <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                                    {translate('resources.charger-firmware.sections.firmware_source')}
                                </Typography>
                            </Box>

                            <FormDataConsumer>
                                {({ formData }) => (
                                    formData?.is_external ? (
                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                            <Chip
                                                icon={<LinkIcon />}
                                                label={translate('resources.charger-firmware.source.url')}
                                                color="primary"
                                                size="small"
                                                sx={{ alignSelf: 'flex-start' }}
                                            />
                                            <TextInput
                                                source="file_url"
                                                label={translate('resources.charger-firmware.fields.file_url')}
                                                helperText={translate('resources.charger-firmware.helpers.file_url')}
                                                validate={required()}
                                                fullWidth
                                            />
                                            <Alert severity="info">
                                                {translate('resources.charger-firmware.helpers.url_warning')}
                                            </Alert>
                                        </Box>
                                    ) : (
                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                            <Chip
                                                icon={<CloudDownload />}
                                                label={translate('resources.charger-firmware.source.upload')}
                                                color="default"
                                                size="small"
                                                sx={{ alignSelf: 'flex-start' }}
                                            />
                                            <Box sx={{ p: 2, border: '1px solid #e0e0e0', borderRadius: 1, backgroundColor: '#FAFAFA' }}>
                                                <FunctionField
                                                    render={(record: any) => (
                                                        <Box sx={{ minWidth: 0 }}>
                                                            <Typography variant="body2" gutterBottom sx={{ wordBreak: 'break-word', overflowWrap: 'anywhere' }}>
                                                                <strong>{translate('resources.charger-firmware.fields.file_name')}:</strong>{' '}
                                                                {record.file_name || 'N/A'}
                                                            </Typography>
                                                            <Typography variant="body2" gutterBottom>
                                                                <strong>{translate('resources.charger-firmware.fields.file_size')}:</strong> {
                                                                    record.file_size
                                                                        ? `${Math.round(record.file_size / 1024)} KB`
                                                                        : 'N/A'
                                                                }
                                                            </Typography>
                                                            {record.file_url && (
                                                                <Link
                                                                    href={record.file_url}
                                                                    target="_blank"
                                                                    rel="noopener noreferrer"
                                                                    sx={{
                                                                        display: 'flex',
                                                                        alignItems: 'center',
                                                                        gap: 1,
                                                                        color: '#00C853',
                                                                        textDecoration: 'none',
                                                                        wordBreak: 'break-word',
                                                                        overflowWrap: 'anywhere',
                                                                        '&:hover': {
                                                                            textDecoration: 'underline',
                                                                        },
                                                                    }}
                                                                >
                                                                    <GetApp fontSize="small" />
                                                                    {translate('resources.charger-firmware.actions.download')}
                                                                </Link>
                                                            )}
                                                        </Box>
                                                    )}
                                                />
                                            </Box>
                                            <Alert severity="warning">
                                                {translate('resources.charger-firmware.edit.fileUploadWarning')}
                                            </Alert>
                                        </Box>
                                    )
                                )}
                            </FormDataConsumer>
                        </CardContent>
                    </Card>

                    {/* Compatibility Section */}
                    <Card sx={{ mb: { xs: 2, sm: 3 }, overflow: 'hidden' }}>
                        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                                <CheckCircle sx={{ mr: 2, color: '#00C853' }} />
                                <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                                    {translate('resources.charger-firmware.sections.compatibility')}
                                </Typography>
                            </Box>

                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                <SelectInput
                                    source="vendor"
                                    label={translate('resources.charger-firmware.fields.vendor')}
                                    helperText={translate('resources.charger-firmware.helpers.vendor')}
                                    choices={vendors.map(v => ({ id: v, name: v }))}
                                    validate={required()}
                                    fullWidth
                                />

                                <FormDataConsumer>
                                    {({ formData }) => {
                                        const availableModels = formData.vendor ? modelsByVendor[formData.vendor] || [] : [];
                                        return (
                                            <SelectInput
                                                source="model"
                                                label={translate('resources.charger-firmware.fields.model')}
                                                helperText={
                                                    formData.vendor
                                                        ? translate('resources.charger-firmware.helpers.model')
                                                        : translate('resources.charger-firmware.helpers.select_vendor_first')
                                                }
                                                choices={availableModels.map(m => ({ id: m, name: m }))}
                                                validate={required()}
                                                disabled={!formData.vendor || availableModels.length === 0}
                                                fullWidth
                                            />
                                        );
                                    }}
                                </FormDataConsumer>

                                <BooleanInput
                                    source="is_active"
                                    label={translate('resources.charger-firmware.fields.is_active')}
                                />
                            </Box>
                        </CardContent>
                    </Card>
                </Box>
            </SimpleForm>
        </Edit>
    );
};

export default FirmwareEdit;
